import { Box } from '@mui/material'
import type { FC } from 'react'
import { FormSubmissionRetrieveResponse } from '@fireflyhealth/api'
import { camelize } from 'humps'

const UNANSWERED_SX = { color: 'grey.500' }

type MultipleChoiceOption = { value: string | number; label: string }

type Question =
  | { type: 'info'; type_options: { content?: string } }
  | {
      type: 'multiple_choice'
      type_options: { otherField?: string | null; choices: MultipleChoiceOption[] }
    }
  | { type: 'choice'; type_options: { choices: MultipleChoiceOption[] } }
  | {
      type: 'text' | 'long_text' | 'rating' | 'calculation' | 'date'
      type_options: Record<string, never>
    }
  | { type: 'address'; type_options: Record<string, never> }
  | { type: 'name'; type_options: Record<string, never> }
  | { type: 'allergy'; type_options: Record<string, never> }
  | { type: 'facility_address'; type_options: Record<string, never> }
  | { type: 'providerInfo'; type_options: Record<string, never> }

function assertQuestion(
  _question: FormSubmissionRetrieveResponse['form']['sections'][0]['questions'][0],
): asserts _question is typeof _question & Question {
  // It's not really worth doing proper validation here...this component is for
  // rendering legacy forms, so we don't expect the schema to change ever again.
  return
}

type MultipleChoiceAnswer = Record<string, string | number | boolean | undefined>

type Medication = { name: string; reactionList: string[]; otherReaction?: string | null }
type AllergyItem = { name: string; reactionList: string[]; otherReaction?: string | null }
type AllergyAnswer = { medication?: Medication[] | null; foodSubstance?: AllergyItem[] | null }

type AddressAnswer = {
  streetAddress?: string
  streetAddress2?: string | null
  city?: string
  state?: string
  zipcode?: string
}

type NameAnswer = {
  first?: string | null
  last?: string | null
  form?: string | null
  medicationName?: string | null
  dose?: string | null
}

type FacilityAddressAnswer = {
  name?: string | null
  city?: string | null
  state?: string | null
}

type ProviderInfoAnswer = {
  providerName?: string | null
  practiceName?: string | null
  specialty?: string | null
  city?: string | null
}

type Answer =
  | string
  | number
  | null
  | MultipleChoiceAnswer
  | AllergyAnswer
  | AddressAnswer
  | NameAnswer
  | FacilityAddressAnswer
  | ProviderInfoAnswer

interface FormAnswerDisplayProps {
  question: FormSubmissionRetrieveResponse['form']['sections'][0]['questions'][0]
  // `undefined` and `null` are different states here: a question the patient was never shown
  // renders a grey dash, one they were shown and left empty renders a plain dash.
  answer: Answer | undefined
}

const FormAnswerDisplay: FC<FormAnswerDisplayProps> = props => {
  const { question, answer } = props
  const isUnanswered = typeof answer === 'undefined'

  assertQuestion(question)

  if (question.type === 'info') {
    if ('content' in question.type_options) {
      return (
        <Box component="p" sx={[isUnanswered && UNANSWERED_SX]} style={{ whiteSpace: 'pre-line' }}>
          {question.type_options.content}
        </Box>
      )
    }
    return null
  }

  // question was not shown to patient
  if (isUnanswered) {
    return (
      <Box component="p" sx={UNANSWERED_SX}>
        -
      </Box>
    )
  }

  // Some submissions store an unanswered question as `{}`.
  if (answer === null || JSON.stringify(answer) === '{}') {
    return <p>-</p>
  }

  if (question.type === 'multiple_choice') {
    const { otherField, choices } = question.type_options
    const mcAnswer = answer as MultipleChoiceAnswer
    // Form submission data multiple choice properties, which must match choice
    // values, were historically camelized by the legacy API client, and we
    // compensated by similarly camelizing the values.
    //
    // While we could choose to simply decamelize the values, the `decamelize`
    // method from `humps` cannot reliably undo camelization done by the
    // backend. For example, `phq9` does not decamelize to `phq_9`.
    //
    // Therefore, we camelize both the values and submission data keys to match.
    // Note that submission data keys have already been camelized upstream.
    const otherValue = otherField && mcAnswer[camelize(otherField)]
    return (
      <>
        <p>
          {choices
            .filter(c => mcAnswer[camelize(c.value.toString())])
            .map(c => c.label)
            .join(', ')}
        </p>
        {otherValue && (
          <p>
            <em>Other:</em>
            <br />
            {otherValue}
          </p>
        )}
      </>
    )
  }

  if (question.type === 'choice') {
    const choice = question.type_options.choices.find(c => c.value.toString() === answer)
    return choice ? (
      <p>
        {choice.label} <i>({choice.value})</i>
      </p>
    ) : null
  }

  if (question.type === 'allergy') {
    const allergyAnswer = answer as AllergyAnswer
    return (
      <Box>
        <p>Medications</p>
        {allergyAnswer.medication ? (
          allergyAnswer.medication.map(medication => (
            <p key={medication.name}>
              {medication.name} - {medication.reactionList.join(', ')}
              {medication.otherReaction ? `(${medication.otherReaction})` : ''}
            </p>
          ))
        ) : (
          <p>None</p>
        )}
        <b>Allergy</b>
        {allergyAnswer.foodSubstance ? (
          allergyAnswer.foodSubstance.map(allergy => (
            <p key={allergy.name}>
              {allergy.name} - {allergy.reactionList.join(', ')}
              {allergy.otherReaction ? ` (${allergy.otherReaction})` : ''}
            </p>
          ))
        ) : (
          <p>None</p>
        )}
      </Box>
    )
  }

  if (
    question.type === 'text' ||
    question.type === 'long_text' ||
    question.type === 'rating' ||
    question.type === 'calculation' ||
    question.type === 'date'
  ) {
    return <p>{answer as string | number}</p>
  }

  if (question.type === 'address') {
    const addr = answer as AddressAnswer
    return (
      <p>
        {addr.streetAddress} {addr.streetAddress2} {addr.city} {addr.state}, {addr.zipcode}
      </p>
    )
  }

  if (question.type === 'name') {
    const nm = answer as NameAnswer
    return (
      <div>
        {nm.first != null && nm.last != null && (
          <p>
            {nm.first} {nm.last}
          </p>
        )}
        {nm.form != null && <p>Forms: {nm.form}</p>}
        {nm.medicationName != null && <p>Medication Name: {nm.medicationName}</p>}
        {nm.dose != null && <p>Dose: {nm.dose}</p>}
      </div>
    )
  }
  if (question.type === 'facility_address') {
    const fa = answer as FacilityAddressAnswer
    return (
      <div>
        <p>Facility Name: {fa.name}</p>
        {fa.city != null && <p>City: {fa.city}</p>}
        {fa.state != null && <p>State: {fa.state}</p>}
      </div>
    )
  }
  if (question.type === 'providerInfo') {
    const pi = answer as ProviderInfoAnswer
    return (
      <div>
        <p>Provider Name: {pi.providerName}</p>
        {pi.practiceName != null && <p>Practice Name: {pi.practiceName}</p>}
        <p>Specialty: {pi.specialty}</p>
        {pi.city != null && <p>City: {pi.city}</p>}
      </div>
    )
  }

  return null
}

export default FormAnswerDisplay
